import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useInView } from 'framer-motion';
import { 
  Menu, X, ArrowRight, Send, Bot, Cpu, BarChart3, Workflow, 
  Code2, Lightbulb, Rocket, CheckCircle2, Mail, Phone, MapPin,
  Linkedin, Twitter, Github, ChevronRight
} from 'lucide-react';
import NeuralNetworkBackground from './components/custom/NeuralNetworkBackground';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import axios from 'axios';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15, delayChildren: 0.1 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// Navigation Component
const Navigation = ({ isScrolled, toggleMenu }) => (
  <motion.nav
    initial={{ y: -100 }}
    animate={{ y: 0 }}
    transition={{ duration: 0.6 }}
    className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled ? 'glass py-4' : 'py-6'
    }`}
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
      <a href="#hero" className="flex items-center gap-2 group" data-testid="logo-link">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center">
          <Bot className="w-5 h-5 text-white" />
        </div>
        <span className="font-display text-2xl font-bold tracking-tight">
          FALOR<span className="gradient-text">-IA</span>
        </span>
      </a>

      <div className="hidden md:flex items-center gap-8">
        {['Services', 'Expertise', 'Processus', 'Contact'].map((item) => (
          <a
            key={item}
            href={`#${item.toLowerCase()}`}
            className="font-body text-white/70 hover:text-white transition-colors link-underline"
            data-testid={`nav-${item.toLowerCase()}`}
          >
            {item}
          </a>
        ))}
        <Button 
          className="btn-glow bg-accent-primary hover:bg-accent-primary/80 text-white rounded-full px-6"
          data-testid="nav-cta-btn"
        >
          Démarrer
        </Button>
      </div>

      <button
        onClick={toggleMenu}
        className="md:hidden p-2 text-white"
        aria-label="Toggle menu"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-6 h-6" />
      </button>
    </div>
  </motion.nav>
);

// Mobile Menu
const MobileMenu = ({ isOpen, toggleMenu }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-[#0a0a0a]"
        data-testid="mobile-menu"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-12">
            <span className="font-display text-2xl font-bold">FALOR-IA</span>
            <button onClick={toggleMenu} className="p-2" data-testid="close-menu-btn">
              <X className="w-6 h-6" />
            </button>
          </div>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            {['Services', 'Expertise', 'Processus', 'Contact'].map((item) => (
              <motion.a
                key={item}
                variants={fadeInUp}
                href={`#${item.toLowerCase()}`}
                onClick={toggleMenu}
                className="block font-display text-4xl text-white/80 hover:text-white transition-colors"
                data-testid={`mobile-nav-${item.toLowerCase()}`}
              >
                {item}
              </motion.a>
            ))}
          </motion.div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// Hero Section
const HeroSection = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/50 to-[#0a0a0a] z-10" />
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover opacity-45"
          poster="https://images.unsplash.com/photo-1555255707-c07966088b7b?w=1920&q=80"
        >
          <source src="https://cdn.coverr.co/videos/coverr-digital-brain-visualization-7395/1080p.mp4" type="video/mp4" />
        </video>
      </motion.div>

      <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          <motion.div variants={fadeIn}>
            <span className="font-mono uppercase text-xs tracking-[0.3em] text-accent-primary">
              Agence IA & Automatisation • Lyon, France
            </span>
          </motion.div>

          <motion.h1
            variants={fadeInUp}
            className="font-display text-5xl md:text-7xl lg:text-8xl font-bold leading-none"
            data-testid="hero-title"
          >
            <span className="italic">Transformez</span> votre
            <br />
            <span className="gradient-text">entreprise</span> avec l'IA
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            className="font-body text-lg md:text-xl text-white/60 max-w-2xl mx-auto"
          >
            Nous concevons des solutions d'intelligence artificielle et d'automatisation sur-mesure 
            pour propulser votre entreprise vers le futur.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 justify-center">
            <Button 
              className="btn-glow bg-white text-[#0a0a0a] hover:bg-white/90 rounded-full px-8 py-6 text-lg font-medium group"
              data-testid="hero-cta-primary"
            >
              Démarrer un projet
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              variant="outline"
              className="rounded-full px-8 py-6 text-lg border-white/20 hover:bg-white/10 text-white"
              data-testid="hero-cta-secondary"
            >
              Découvrir nos services
            </Button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 rounded-full bg-accent-primary"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Marquee Section with AI Service Images
const MarqueeSection = () => {
  const aiServices = [
    {
      image: "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=400&q=80",
      label: "Assistant IA"
    },
    {
      image: "https://images.unsplash.com/photo-1587560699334-cc4ff634909a?w=400&q=80",
      label: "Robotique Médicale"
    },
    {
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400&q=80",
      label: "IA Finance"
    },
    {
      image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&q=80",
      label: "Automatisation"
    },
    {
      image: "https://images.unsplash.com/photo-1580894894513-541e068a3e2b?w=400&q=80",
      label: "IA Industrielle"
    },
    {
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&q=80",
      label: "Data Analytics"
    }
  ];

  return (
    <section className="py-6 overflow-hidden border-y border-white/10 bg-surface/50">
      <div className="animate-marquee flex">
        {[...Array(3)].map((_, setIndex) => (
          <div key={setIndex} className="flex items-center gap-6 px-3">
            {aiServices.map((service, i) => (
              <div 
                key={`${setIndex}-${i}`} 
                className="relative group flex-shrink-0"
              >
                <div className="w-48 h-32 md:w-64 md:h-40 rounded-xl overflow-hidden border border-white/10 group-hover:border-accent-primary/50 transition-all duration-300">
                  <img
                    src={service.image}
                    alt={service.label}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <span className="font-mono uppercase text-xs tracking-wider text-accent-primary">IA Service</span>
                    <p className="font-body text-sm text-white font-medium">{service.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};

// About Section with animated stats
const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const stats = [
    { value: '98%', label: 'Taux de satisfaction' },
    { value: '-70%', label: 'Tâches manuelles' },
    { value: '3x', label: 'Plus rapide' },
    { value: '50+', label: 'Projets livrés' }
  ];

  return (
    <section id="about" className="py-24 md:py-32 relative" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={staggerContainer}
            className="space-y-8"
          >
            <motion.span variants={fadeIn} className="font-mono uppercase text-xs tracking-[0.2em] text-accent-primary">
              A propos
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl font-bold leading-tight" data-testid="about-title">
              L'IA au service de <span className="italic gradient-text">votre performance</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="font-body text-lg text-white/60 leading-relaxed">
              FALOR-IA est une agence lyonnaise spécialisée dans l'intelligence artificielle et l'automatisation. 
              Nous accompagnons les entreprises dans leur transformation digitale en développant des solutions 
              innovantes et personnalisées.
            </motion.p>
            <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-6">
              {stats.map((stat, i) => (
                <div key={i} className="p-4 rounded-2xl bg-surface border border-white/5">
                  <div className="font-display text-3xl md:text-4xl font-bold gradient-text">{stat.value}</div>
                  <div className="font-body text-sm text-white/50">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden aspect-[4/5]">
                  <img
                    src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&q=80"
                    alt="AI Technology"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden aspect-square glass p-6 flex flex-col justify-end">
                  <Bot className="w-10 h-10 text-accent-primary mb-4" />
                  <p className="font-body text-sm text-white/70">Solutions IA sur-mesure</p>
                </div>
              </div>
              <div className="space-y-4 pt-12">
                <div className="rounded-2xl overflow-hidden aspect-square glass p-6">
                  <div className="font-display text-5xl font-bold gradient-text mb-2">24/7</div>
                  <p className="font-body text-sm text-white/70">Automatisation continue</p>
                </div>
                <div className="rounded-2xl overflow-hidden aspect-[4/5]">
                  <img
                    src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=600&q=80"
                    alt="Robot AI"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>
            {/* Decorative element */}
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-accent-primary/10 rounded-full blur-[100px]" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services/Expertise Section with Bento Grid
const ExpertiseSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const services = [
    { 
      icon: Bot, 
      title: 'Chatbots IA', 
      description: 'Assistants virtuels intelligents pour automatiser votre service client',
      span: 'col-span-12 md:col-span-6 lg:col-span-4'
    },
    { 
      icon: Workflow, 
      title: 'Workflows Automatisés', 
      description: 'Automatisation de vos processus métier de bout en bout',
      span: 'col-span-12 md:col-span-6 lg:col-span-4'
    },
    { 
      icon: BarChart3, 
      title: 'Business Intelligence', 
      description: 'Tableaux de bord et analyses prédictives pour des décisions éclairées',
      span: 'col-span-12 md:col-span-6 lg:col-span-4'
    },
    { 
      icon: Cpu, 
      title: 'Intégration LLM', 
      description: 'Déploiement de modèles de langage avancés (GPT, Claude, Gemini)',
      span: 'col-span-12 md:col-span-6 lg:col-span-6',
      featured: true
    },
    { 
      icon: Code2, 
      title: 'APIs & Connecteurs', 
      description: 'Connexion de vos outils et systèmes existants',
      span: 'col-span-12 md:col-span-6 lg:col-span-6'
    }
  ];

  return (
    <section id="services" className="py-24 md:py-32 bg-surface" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span variants={fadeIn} className="font-mono uppercase text-xs tracking-[0.2em] text-accent-primary">
            Nos Services
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl font-bold mt-4" data-testid="services-title">
            Expertise <span className="italic gradient-text">complète</span>
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid grid-cols-12 gap-4 md:gap-6"
        >
          {services.map((service, i) => (
            <motion.div
              key={i}
              variants={scaleIn}
              className={`${service.span} p-6 md:p-8 rounded-2xl bg-[#0a0a0a] border border-white/5 card-lift group`}
              data-testid={`service-card-${i}`}
            >
              <div className={`w-14 h-14 rounded-xl ${service.featured ? 'bg-gradient-to-br from-accent-primary to-accent-secondary' : 'bg-surface-highlight'} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                <service.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-display text-xl md:text-2xl font-bold mb-3">{service.title}</h3>
              <p className="font-body text-white/60">{service.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Impact Section with animated counters
const ImpactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const impacts = [
    { number: '10M+', label: 'Tâches automatisées', description: 'par mois pour nos clients' },
    { number: '85%', label: 'Gain de temps', description: 'sur les processus répétitifs' },
    { number: '€2M+', label: 'Économies générées', description: 'pour nos partenaires' },
    { number: '24/7', label: 'Disponibilité', description: 'de vos solutions IA' }
  ];

  return (
    <section id="expertise" className="py-24 md:py-32 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 bg-gradient-to-b from-surface via-[#0a0a0a] to-[#0a0a0a]" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span variants={fadeIn} className="font-mono uppercase text-xs tracking-[0.2em] text-accent-primary">
            Impact
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl font-bold mt-4" data-testid="impact-title">
            Résultats <span className="italic gradient-text">mesurables</span>
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {impacts.map((impact, i) => (
            <motion.div
              key={i}
              variants={scaleIn}
              className="p-8 rounded-2xl glass text-center animated-border"
              data-testid={`impact-card-${i}`}
            >
              <div className="font-display text-4xl md:text-5xl font-bold gradient-text mb-2 glow-text">
                {impact.number}
              </div>
              <div className="font-body font-semibold text-white mb-1">{impact.label}</div>
              <div className="font-body text-sm text-white/50">{impact.description}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Featured image */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 rounded-3xl overflow-hidden relative group"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent z-10" />
          <img
            src="https://images.unsplash.com/photo-1555255707-c07966088b7b?w=1600&q=80"
            alt="AI Robot Visualization"
            className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute bottom-8 left-8 z-20">
            <p className="font-display text-2xl md:text-3xl font-bold">
              L'intelligence artificielle au service de <span className="gradient-text">l'humain</span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const steps = [
    { number: '01', title: 'Audit', description: 'Analyse approfondie de vos besoins et de votre existant', icon: Lightbulb },
    { number: '02', title: 'Conception', description: 'Définition de la stratégie et architecture de la solution', icon: Code2 },
    { number: '03', title: 'Développement', description: 'Création itérative avec validation continue', icon: Cpu },
    { number: '04', title: 'Déploiement', description: 'Mise en production et accompagnement personnalisé', icon: Rocket }
  ];

  return (
    <section id="processus" className="py-24 md:py-32 bg-surface" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span variants={fadeIn} className="font-mono uppercase text-xs tracking-[0.2em] text-accent-primary">
            Notre Processus
          </motion.span>
          <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl font-bold mt-4" data-testid="process-title">
            Une approche <span className="italic gradient-text">structurée</span>
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {steps.map((step, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              className="relative p-8 rounded-2xl bg-[#0a0a0a] border border-white/5 group card-lift"
              data-testid={`process-step-${i}`}
            >
              <div className="font-mono text-6xl font-bold text-white/5 absolute top-4 right-4">
                {step.number}
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <step.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-display text-xl font-bold mb-3">{step.title}</h3>
              <p className="font-body text-white/60 text-sm">{step.description}</p>
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 z-10">
                  <ChevronRight className="w-6 h-6 text-accent-primary" />
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
    }
    
    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative bg-[#0a0a0a]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={staggerContainer}
            className="space-y-8"
          >
            <motion.span variants={fadeIn} className="font-mono uppercase text-xs tracking-[0.2em] text-accent-primary">
              Contact
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl font-bold" data-testid="contact-title">
              Parlons de votre <span className="italic gradient-text">projet</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="font-body text-lg text-white/60">
              Prêt à transformer votre entreprise avec l'IA? Contactez-nous pour discuter 
              de vos besoins et découvrir comment nous pouvons vous aider.
            </motion.p>

            <motion.div variants={fadeInUp} className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-surface flex items-center justify-center">
                  <Mail className="w-5 h-5 text-accent-primary" />
                </div>
                <div>
                  <p className="font-body text-sm text-white/50">Email</p>
                  <p className="font-body text-white">florian.folio97400@gmail.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-surface flex items-center justify-center">
                  <Phone className="w-5 h-5 text-accent-primary" />
                </div>
                <div>
                  <p className="font-body text-sm text-white/50">Téléphone</p>
                  <p className="font-body text-white">06 29 72 12 24</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-surface flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-accent-primary" />
                </div>
                <div>
                  <p className="font-body text-sm text-white/50">Adresse</p>
                  <p className="font-body text-white">Lyon & Île de la Réunion, France</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {submitted ? (
              <div className="h-full flex items-center justify-center p-12 rounded-2xl glass text-center">
                <div>
                  <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-6" />
                  <h3 className="font-display text-2xl font-bold mb-2">Message envoyé!</h3>
                  <p className="font-body text-white/60">Nous vous répondrons dans les plus brefs délais.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-8 rounded-2xl glass space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-sm text-white/50 mb-2 block">Nom</label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-surface border-white/10 text-white focus:border-accent-primary"
                      placeholder="Jean Dupont"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm text-white/50 mb-2 block">Email</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-surface border-white/10 text-white focus:border-accent-primary"
                      placeholder="jean@entreprise.fr"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-body text-sm text-white/50 mb-2 block">Entreprise</label>
                  <Input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="bg-surface border-white/10 text-white focus:border-accent-primary"
                    placeholder="Nom de votre entreprise"
                    data-testid="contact-company-input"
                  />
                </div>
                <div>
                  <label className="font-body text-sm text-white/50 mb-2 block">Message</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="bg-surface border-white/10 text-white focus:border-accent-primary min-h-[150px]"
                    placeholder="Décrivez votre projet..."
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-glow bg-gradient-to-r from-accent-primary to-accent-secondary hover:opacity-90 text-white rounded-full py-6 text-lg font-medium"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                  <Send className="ml-2 w-5 h-5" />
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => (
  <footer className="py-16 border-t border-white/10 bg-[#0a0a0a] relative z-20">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-4 gap-12 mb-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-2xl font-bold">FALOR-IA</span>
          </div>
          <p className="font-body text-white/50 max-w-md mb-6">
            Agence d'intelligence artificielle et d'automatisation basée à Lyon et à l'Île de la Réunion. 
            Nous transformons votre entreprise avec des solutions IA sur-mesure.
          </p>
          <div className="flex gap-4">
            {[Linkedin, Twitter, Github].map((Icon, i) => (
              <a
                key={i}
                href="#"
                className="w-10 h-10 rounded-full bg-surface flex items-center justify-center hover:bg-accent-primary/20 transition-colors"
                data-testid={`social-link-${i}`}
              >
                <Icon className="w-5 h-5 text-white/70" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-body font-semibold mb-4">Navigation</h4>
          <ul className="space-y-3">
            {['Services', 'Expertise', 'Processus', 'Contact'].map((item) => (
              <li key={item}>
                <a href={`#${item.toLowerCase()}`} className="font-body text-white/50 hover:text-white transition-colors">
                  {item}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-body font-semibold mb-4">Contact</h4>
          <ul className="space-y-3 font-body text-white/50">
            <li>Lyon & Île de la Réunion</li>
            <li>florian.folio97400@gmail.com</li>
            <li>06 29 72 12 24</li>
          </ul>
        </div>
      </div>

      <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-center items-center gap-4">
        <p className="font-body text-sm text-white/30">
          © 2025 FALOR-IA. Tous droits réservés.
        </p>
      </div>
    </div>
  </footer>
);

// Main App Component
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  return (
    <div className="relative">
      <NeuralNetworkBackground />
      <Navigation isScrolled={isScrolled} toggleMenu={toggleMenu} />
      <MobileMenu isOpen={menuOpen} toggleMenu={toggleMenu} />
      
      <main className="relative z-10">
        <HeroSection />
        <MarqueeSection />
        <AboutSection />
        <ExpertiseSection />
        <ImpactSection />
        <ProcessSection />
        <ContactSection />
      </main>
      
      <Footer />
    </div>
  );
}

export default App;
